/**
 * A member's face, on the board.
 *
 * The panel has shown faces since avatars existed and the board never has, so
 * the same unit read as two different products: names alone on one screen,
 * names with faces on the other. The state endpoint has carried `avatarUrl` for
 * every member all along.
 *
 * Same construction as the panel's: initials underneath, image over the top, so
 * a missing or broken avatar degrades to something readable rather than an empty
 * circle. The URL is validated server-side and is only ever an https link.
 */
export function MuFace({
  username,
  avatarUrl,
  online,
  size = "md",
  level = null,
}: {
  username: string;
  avatarUrl?: string | null;
  online?: boolean;
  size?: "sm" | "md" | "lg";
  /** Their level, when it is worth showing. On the small faces in the ledger the
   * level is a token beside the name; on the larger ones the design puts it on
   * the ring itself, which is how the game shows it. */
  level?: number | null;
}) {
  return (
    <span
      className={`face face-${size}${online ? " face-online" : ""}`}
      aria-hidden="true"
      title={username}
    >
      <span className="face-initials">{initials(username)}</span>
      {avatarUrl ? <img className="face-img" alt="" loading="lazy" src={avatarUrl} /> : null}
      {level !== null && size !== "sm" ? <span className="face-level">{level}</span> : null}
    </span>
  );
}

/**
 * One letter, or two when the name gives two words to work with.
 *
 * Character for character the same rule as MUQ_FORMAT.initials in the
 * extension's format.js — including splitting on spaces, underscores and
 * hyphens but NOT on dots — because a member who reads both surfaces must not
 * see two different marks for the same name.
 */
export function initials(username: string): string {
  const words = String(username || "?")
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
